"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import styles from "./ageCalc.module.css";

function getTodayDateString(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseDate(s: string): Date | null {
  if (!s || s.length < 10) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function getDayOfWeek(date: Date): string {
  return date.toLocaleDateString("en-US", { weekday: "long" });
}

export type AgeResult = {
  years: number;
  months: number;
  days: number;
  totalDays: number;
  totalHours: number;
  totalMinutes: number;
  weeksLived: number;
  dayOfWeekBorn: string;
  nextBirthdayInDays: number | null;
  nextBirthdayDate: string | null;
  ageString: string;
};

function calculateAge(dobStr: string, asOfStr: string): AgeResult | null {
  const dob = parseDate(dobStr);
  const asOf = parseDate(asOfStr);
  if (!dob || !asOf) return null;
  if (asOf.getTime() < dob.getTime()) return null;

  let years = asOf.getFullYear() - dob.getFullYear();
  let months = asOf.getMonth() - dob.getMonth();
  let days = asOf.getDate() - dob.getDate();

  if (days < 0) {
    months -= 1;
    const lastDay = new Date(asOf.getFullYear(), asOf.getMonth(), 0).getDate();
    days += lastDay;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const totalMs = asOf.getTime() - dob.getTime();
  const totalDays = Math.floor(totalMs / (24 * 60 * 60 * 1000));
  const totalHours = Math.floor(totalMs / (60 * 60 * 1000));
  const totalMinutes = Math.floor(totalMs / (60 * 1000));
  const weeksLived = Math.floor(totalDays / 7);

  const dayOfWeekBorn = getDayOfWeek(dob);

  const nextBday = new Date(asOf.getFullYear(), dob.getMonth(), dob.getDate());
  if (nextBday.getTime() <= asOf.getTime())
    nextBday.setFullYear(asOf.getFullYear() + 1);
  const nextBirthdayInDays = Math.ceil(
    (nextBday.getTime() - asOf.getTime()) / (24 * 60 * 60 * 1000)
  );
  const nextBirthdayDate = nextBday.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const ageString =
    years === 0 && months === 0 && days === 0
      ? "0 days"
      : [
          years > 0 && `${years} year${years !== 1 ? "s" : ""}`,
          months > 0 && `${months} month${months !== 1 ? "s" : ""}`,
          days > 0 && `${days} day${days !== 1 ? "s" : ""}`,
        ]
          .filter(Boolean)
          .join(" ");

  return {
    years,
    months,
    days,
    totalDays,
    totalHours,
    totalMinutes,
    weeksLived,
    dayOfWeekBorn,
    nextBirthdayInDays,
    nextBirthdayDate,
    ageString,
  };
}

export default function FileAgeCalc() {
  const todayStr = getTodayDateString();
  const [date, setDate] = useState({ dob: "1990-01-15", ageDate: todayStr });
  const [errors, setErrors] = useState<{ dob?: string; ageDate?: string }>({});
  const [hasCalculated, setHasCalculated] = useState(false);

  const ageResult = useMemo(() => {
    const result = calculateAge(date.dob, date.ageDate);
    return result;
  }, [date.dob, date.ageDate]);

  const isValid = ageResult !== null;

  useEffect(() => {
    setDate((prev) => ({ ...prev, ageDate: getTodayDateString() }));
  }, []);

  const validateForm = useCallback((): boolean => {
    const err: { dob?: string; ageDate?: string } = {};
    if (!date.dob?.trim()) err.dob = "Date of birth is required";
    if (!date.ageDate?.trim()) err.ageDate = "Age as of date is required";
    if (date.dob && date.ageDate) {
      const dob = parseDate(date.dob);
      const asOf = parseDate(date.ageDate);
      if (dob && asOf && asOf.getTime() < dob.getTime())
        err.ageDate = "Age as of date must be on or after date of birth";
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  }, [date.dob, date.ageDate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDate((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const setAgeDateToToday = () => {
    setDate((prev) => ({ ...prev, ageDate: getTodayDateString() }));
    setErrors((prev) => ({ ...prev, ageDate: undefined }));
  };

  const handleCalculate = () => {
    if (!validateForm()) {
      toast.error("Please fix the errors and try again.");
      return;
    }
    if (!ageResult) {
      toast.error("Please enter valid dates.");
      return;
    }
    setHasCalculated(true);
  };

  return (
    <div className={styles.wrapper}>
      <div className={`globally-age-calculter ${styles.formCard}`}>
        <div className={styles.formRow}>
          <div className="age-input">
            <label className="bottom-text" htmlFor="dob">
              Date of Birth
            </label>
            <div className="age-input">
              <input
                name="dob"
                id="dob"
                type="date"
                value={date.dob}
                onChange={handleChange}
                className={errors?.dob ? "error-input" : ""}
                max={date.ageDate || undefined}
                aria-invalid={!!errors?.dob}
              />
            </div>
            {errors?.dob && (
              <span className={styles.errorText} role="alert">
                {errors.dob}
              </span>
            )}
          </div>
        </div>

        <div className="three-col-grid" aria-hidden="true" />

        <div className={styles.formRow}>
          <div className="age-input">
            <div className={styles.labelRow}>
              <label className="bottom-text" htmlFor="ageDate">
                Age as of (or current date)
              </label>
              <button
                type="button"
                className={styles.setTodayBtn}
                onClick={setAgeDateToToday}
              >
                Set to today
              </button>
            </div>
            <div className="age-input">
              <input
                name="ageDate"
                id="ageDate"
                type="date"
                value={date.ageDate}
                onChange={handleChange}
                className={errors?.ageDate ? "error-input" : ""}
                min={date.dob || undefined}
                aria-invalid={!!errors?.ageDate}
              />
            </div>
            {errors?.ageDate && (
              <span className={styles.errorText} role="alert">
                {errors.ageDate}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="gst-two-button-center-alignment">
        <div className="button">
          <button type="button" onClick={handleCalculate}>
            Calculate age
          </button>
        </div>
      </div>

      {hasCalculated && ageResult && (
        <div className={styles.results}>
          <div className="gst-result-box">
            <div className="text-alignment">
              <p>Your age</p>
              <span className={styles.mainAge}>{ageResult.ageString}</span>
            </div>
          </div>

          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Born on</span>
              <span className={styles.statValue}>{ageResult.dayOfWeekBorn}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Total days lived</span>
              <span className={styles.statValue}>
                {ageResult.totalDays.toLocaleString()}
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Weeks lived</span>
              <span className={styles.statValue}>
                {ageResult.weeksLived.toLocaleString()}
              </span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Hours lived</span>
              <span className={styles.statValue}>
                {ageResult.totalHours.toLocaleString()}
              </span>
            </div>
            {ageResult.nextBirthdayInDays !== null && (
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Next birthday</span>
                <span className={styles.statValue}>
                  in {ageResult.nextBirthdayInDays} day
                  {ageResult.nextBirthdayInDays !== 1 ? "s" : ""}
                </span>
                <span className={styles.statSub}>
                  {ageResult.nextBirthdayDate}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {!hasCalculated && (
        <div className="gst-result-box">
          <div className="text-alignment">
            <span>
              Enter your date of birth and the date to calculate your age as of
              (default is today). Click &quot;Calculate age&quot; to see results.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
